import { hasDriverName } from '../../Utils/driverName';

/**
 * Driver PERSON LINKS, as the office's driver screen sees them.
 *
 * A driver row belongs to exactly ONE operator record. A company that
 * settles in two currencies is two records on purpose (operatorGroups), so
 * the same human hired by that company holds TWO driver rows, one under
 * each, with different `Id`s. `person_id` (DriverSchema) is the office
 * saying "these rows are one human".
 *
 * It is ADMIN-SET AND NOTHING ELSE. Nothing here — and nothing in the API —
 * infers it from a name, a phone number or any other resemblance: two
 * drivers called Kovács Anna are two people until somebody who knows says
 * otherwise, and what hangs off the link is a login to somebody's work.
 * There is no Person table and no list of people to pick from; the id is an
 * opaque grouping key, borrowed from the row you point at or minted when
 * neither row has one.
 *
 * The collapsing rules below are a PORT of ph-core `driverPerson.helper.ts`
 * (`nameCompleteness` / `representativeDriver` / `onePerPerson`), which the
 * operator's own portal roster already runs on. They are duplicated rather
 * than re-derived so that one human reads as one person on both screens;
 * `onePerPerson` here must stay answer-for-answer identical to that one.
 */

/** A row's link, as a string, with "no link" spelled one way: ''. */
export const personIdOf = (driver) => (driver?.person_id ? String(driver.person_id) : '');

/** The id spelling driver rows use. */
export const driverId = (driver) => String(driver?.Id || '');

/**
 * How complete a row's name is — the tie-break for which of a person's rows
 * represents them. Higher is better.
 *
 * Both halves entered beats a `full_name` alone beats nothing; a longer
 * `full_name` beats a shorter one, because the row that was filled in
 * properly is the one the office recognises on a roster.
 */
const nameCompleteness = (row) => (
  (String(row?.first_name ?? '').trim() ? 2 : 0)
  + (String(row?.last_name ?? '').trim() ? 2 : 0)
  + (String(row?.full_name ?? '').trim() ? 1 : 0)
);

/**
 * Which of a person's rows stands for them on a screen.
 *
 * The order is DELIBERATE and total, so the same person is represented by
 * the same row on every render and on the operator's own portal roster:
 *
 *  1. the row under the SURVIVING operator record — `preferredOperatorIds`
 *     is the company's own record set with its primary first
 *     (operatorGroups), so this prefers the record the company is now
 *     managed under, and only then the retired half of the pair;
 *  2. the most COMPLETE name, because the duplicate rows in production are
 *     not equally well filled in — one half of a pair was typically typed
 *     in a hurry;
 *  3. the lowest `Id`. Nothing to recommend it except that it is stable:
 *     without a final tie-break two equally good rows could swap places
 *     between renders, and a list that reorders itself is a list somebody
 *     reports as a bug.
 */
export function representativeDriver(rows, preferredOperatorIds = []) {
  const rank = (row) => {
    const operatorRank = preferredOperatorIds.indexOf(String(row?.Operator_Id));

    return [
      // Not in the preferred list at all sorts last, not first.
      operatorRank === -1 ? Number.MAX_SAFE_INTEGER : operatorRank,
      -nameCompleteness(row),
      driverId(row),
    ];
  };

  return [...(rows || [])].sort((a, b) => {
    const [aOperator, aName, aId] = rank(a);
    const [bOperator, bName, bId] = rank(b);

    return (aOperator - bOperator) || (aName - bName) || aId.localeCompare(bId);
  })[0];
}

/**
 * One entry per HUMAN, carrying every row that human holds.
 *
 * Rows sharing a `person_id` collapse to a single entry represented by
 * `representativeDriver`; rows carrying none are kept exactly as they are,
 * every one of them.
 *
 * That last part is the whole safety property. "No `person_id`" means the
 * office has NOT said these are the same person, and two different drivers
 * of one company must never be merged into one entry on a guess — so
 * nothing here looks at a name or a phone number.
 *
 * Order is preserved: a person's entry sits where its FIRST row sat, so a
 * list that was sorted stays sorted.
 */
export function personEntries(rows, preferredOperatorIds = []) {
  const present = (rows || []).filter(Boolean);
  const byPerson = new Map();

  present.forEach((row) => {
    const personId = personIdOf(row);

    if (personId) {
      byPerson.set(personId, [...(byPerson.get(personId) || []), row]);
    }
  });

  const emitted = new Set();

  return present.flatMap((row) => {
    const personId = personIdOf(row);

    // Unlinked: kept, always, and never collapsed against anything.
    if (!personId) {
      return [{
        key: `row:${driverId(row)}`,
        personId: '',
        rows: [row],
        representative: row,
        shared: false,
      }];
    }

    if (emitted.has(personId)) {
      return [];
    }

    emitted.add(personId);

    const group = byPerson.get(personId) || [row];

    return [{
      key: `person:${personId}`,
      personId,
      rows: group,
      representative: representativeDriver(group, preferredOperatorIds) || row,
      // More than one row of this person is VISIBLE here. A person whose
      // other rows sit outside this company reads as a single row, which is
      // all this screen can honestly say about them.
      shared: group.length > 1,
    }];
  });
}

/**
 * One row per HUMAN — the exact answer ph-core's `onePerPerson` gives for
 * the same input, which is what the operator's portal roster shows.
 */
export const onePerPerson = (rows, preferredOperatorIds = []) => (
  personEntries(rows, preferredOperatorIds).map((entry) => entry.representative)
);

/**
 * A fresh opaque grouping key.
 *
 * There is no Person table to take an id from, so a link between two rows
 * that both carry nothing has to mint one. A UUID and not something derived
 * from the two rows: the key must not stop meaning anything if one of the
 * rows it was minted from is later unlinked or deleted. 36 characters,
 * inside the API's `max(64)`.
 */
export const mintPersonId = () => {
  // Spelled the way this directory's other editors mint their keys
  // (OperatorOnlyRoutesEditor, OperatorOnlyZonesEditor): `crypto` is the
  // browser global, guarded by `typeof` because jsdom and the older Safari
  // the office still opens the dashboard on do not always carry
  // `randomUUID`. `globalThis` is not reachable under this repo's eslint env.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * The body that writes ONE row's link.
 *
 * `DriverInput` re-reads the whole row on every write, so a link has to
 * carry the name, the phone and the active flag back unchanged — this is an
 * upsert, not a patch.
 *
 * `operator_id` is the ROW'S OWN record and never the one whose modal is
 * open. The screen lists both records of a company, so posting the open
 * record here would silently RE-HOME a sibling row — moving a driver to the
 * other currency bucket as a side effect of linking them.
 *
 * `person_id` is always present in this body, including as '' — that is the
 * unlink, and the API distinguishes absent (leave alone) from empty
 * (remove) on exactly this field.
 */
export const personLinkSave = (driver, personId) => ({
  Id: driverId(driver),
  first_name: driver?.first_name || '',
  last_name: driver?.last_name || '',
  phone_number: driver?.phone_number,
  operator_id: String(driver?.Operator_Id || ''),
  active: Number(driver?.active) === 1 ? 1 : 0,
  person_id: personId,
});

/**
 * Why a row cannot be written at all right now, or '' when it can.
 *
 * Both halves of the name are required by `DriverInput` on EVERY write, and
 * a link or an unlink is a write of the whole row. 54 active rows in
 * production still carry only one half, so pointing one of them at another
 * driver would earn a 400 and no link — better said here, against the row,
 * than as a failed request the office has to interpret.
 */
const nameBlocker = (driver) => (hasDriverName(driver)
  ? ''
  : `${driver?.full_name || 'This driver'} needs a first and a last name before their link can be changed.`);

/**
 * What linking `source` to `target` would write, or why it is refused.
 *
 * The rule, in order:
 *
 *  1. the TARGET's key wins when it has one — the row you pointed at is the
 *     person you named, so `source` joins them and the target is not
 *     rewritten;
 *  2. otherwise the SOURCE's key, for the same reason mirrored;
 *  3. otherwise a fresh key, written to BOTH rows.
 *
 * Two rows that ALREADY carry different keys are REFUSED rather than
 * merged. Merging means rewriting every row of one of the two people, and
 * this screen only ever sees one company's rows — `person_id` is global and
 * hand-set, so a person's rows are not guaranteed to be wholly visible
 * here. A merge that missed a row would leave one human split across two
 * keys, silently, which is worse than being told to unlink first.
 */
export function planPersonLink(source, target, mint = mintPersonId) {
  if (!driverId(source) || !driverId(target)) {
    return { personId: '', writes: [], blocked: 'Pick the driver this one is the same person as.' };
  }

  if (driverId(source) === driverId(target)) {
    return { personId: '', writes: [], blocked: 'A driver is already the same person as themselves.' };
  }

  const blockedName = nameBlocker(source) || nameBlocker(target);

  if (blockedName) {
    return { personId: '', writes: [], blocked: blockedName };
  }

  const sourceKey = personIdOf(source);
  const targetKey = personIdOf(target);

  if (sourceKey && targetKey && sourceKey === targetKey) {
    return { personId: sourceKey, writes: [], blocked: 'These two rows are already the same person.' };
  }

  if (sourceKey && targetKey) {
    return {
      personId: '',
      writes: [],
      blocked: 'Both of these are already linked to someone, and to different people. '
        + 'Unlink one of them first, then link it to the other.',
    };
  }

  const personId = targetKey || sourceKey || mint();

  return {
    personId,
    // Only the rows that actually change. The row that already carries the
    // key is left untouched, so a link costs one write and not two.
    writes: [source, target]
      .filter((row) => personIdOf(row) !== personId)
      .map((row) => personLinkSave(row, personId)),
    blocked: '',
  };
}

/**
 * What unlinking ONE row would write, or why it is refused.
 *
 * The EMPTY STRING is sent deliberately and must never become an omitted
 * field: the API leaves the link alone when `person_id` is absent and
 * removes it when the value is empty. Omitting it here would make Unlink a
 * button that reports success and does nothing.
 *
 * Only the row named is unlinked. The others keep the key — a person left
 * holding a single row reads exactly like an unlinked one everywhere, so
 * there is nothing to tidy up and nothing to write on somebody else's row.
 */
export function planPersonUnlink(driver) {
  if (!driverId(driver)) {
    return { writes: [], blocked: 'Pick a driver to unlink.' };
  }

  if (!personIdOf(driver)) {
    return { writes: [], blocked: 'This driver is not linked to anyone.' };
  }

  const blockedName = nameBlocker(driver);

  if (blockedName) {
    return { writes: [], blocked: blockedName };
  }

  return { writes: [personLinkSave(driver, '')], blocked: '' };
}

/**
 * The OTHER rows of this human that removing `driver` will leave running.
 *
 * The office's own Remove is `softDeleteDriver`, which switches off the ONE
 * row it names and cascades to nothing — unlike the operator portal's
 * Remove (`softDeleteDriverForOperator`), which deactivates every sibling
 * because the operator was only ever shown one entry for the person.
 *
 * So on THIS screen the hazard runs the other way: the office removes a
 * driver, and the human keeps a live row, keeps their portal login — which
 * hangs off one specific row and is checked against that row's `active` —
 * and goes on seeing the work. Only ACTIVE rows are counted, because an
 * already-inactive sibling is not holding anything open.
 */
export const activeSiblings = (driver, rows) => (rows || []).filter((row) => (
  personIdOf(row)
  && personIdOf(row) === personIdOf(driver)
  && driverId(row) !== driverId(driver)
  && Number(row?.active) === 1
));
